import { isValidTimeZone } from './localTime.js';
import { resolveTimeZoneFromAddress } from './regionTimeZone.js';
/**
 * Expected dates on the quote, the job and the customer's emails (Warren, 21 October 2026): "Expected ship date is the
 * PO release date plus the lead time. Expected delivery adds the transit time from the freight quote. Show both ... as
 * 'about Nov 18'." One pure function, so the job page, the quote, its PDF, the emails and the moved-delivery check all
 * read the same dates.
 *
 *   - Ship date: Autel's confirmed ready date when it gave one; otherwise the PO release date (today, before release)
 *     plus the longest catalog lead time among the lines. A line without a lead time means no date rather than a
 *     wrong one: "Ship date confirmed when Autel accepts the order" (Warren, 21 October 2026: "Never guess a date").
 *     A date already past while nothing has shipped reads as today.
 *   - Once a package ships: the shipped date, and the carrier's latest estimate across the packages still on their way.
 *   - Delivery: the carrier's estimate, else the ship date plus the freight quote's transit days. Without either, the
 *     ship date shows alone and delivery follows the freight quote.
 *
 * Calendar days throughout, in the site's time zone when it is known, else ET.
 */
export const EXPECTED_DATES_DEFAULT_TIME_ZONE = 'America/New_York';
/** Warren, 21 October 2026: "Pat and Linda get one email if delivery moves by more than 2 days." */
export const DELIVERY_MOVED_DAYS = 2;
/**
 * Warren, 21 October 2026: "Until a SKU has one, show 'Ship date confirmed when Autel accepts the order'. Never guess a
 * date." Shown, verbatim, whenever a line has no lead time and Autel gave no ready date.
 */
export const SHIP_DATE_PENDING_TEXT = 'Ship date confirmed when Autel accepts the order';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 86_400_000;
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const dayFormat = new Map();
/** The zone to read dates in: the given one when this runtime knows it, else ET. */
export function datesTimeZone(timeZone) {
    return timeZone && isValidTimeZone(timeZone) ? timeZone : EXPECTED_DATES_DEFAULT_TIME_ZONE;
}
/** A site's zone from its address (state or province, longitude for a split one), else ET. */
export function siteTimeZone(site) {
    return datesTimeZone(site ? resolveTimeZoneFromAddress(site).timezone : null);
}
/** The calendar day (YYYY-MM-DD) of an instant in a zone. A bare YYYY-MM-DD is already a calendar day. */
export function zonedDay(at, timeZone) {
    if (typeof at === 'string' && YMD.test(at))
        return at;
    const zone = datesTimeZone(timeZone);
    let f = dayFormat.get(zone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
        dayFormat.set(zone, f);
    }
    const p = Object.fromEntries(f.formatToParts(new Date(at)).map(x => [x.type, x.value]));
    return `${p.year}-${p.month}-${p.day}`;
}
const utc = (ymd) => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)));
/** A calendar day plus whole days. */
export function addDays(ymd, days) {
    return new Date(utc(ymd) + Math.round(days) * DAY_MS).toISOString().slice(0, 10);
}
/** Whole days from one calendar day to another (negative when earlier). */
export function daysBetween(from, to) {
    return Math.round((utc(to) - utc(from)) / DAY_MS);
}
/** True when a delivery moved more than 2 days, later or earlier, from the date last told. */
export function deliveryMoved(told, now) {
    return Math.abs(daysBetween(told, now)) > DELIVERY_MOVED_DAYS;
}
/** "Nov 18": month short name and day; the year only when it is not the current year in that zone. */
export function formatDay(ymd, now = new Date(), timeZone) {
    const year = ymd.slice(0, 4), label = `${MONTHS[Number(ymd.slice(5, 7)) - 1]} ${Number(ymd.slice(8, 10))}`;
    return year === zonedDay(now, timeZone).slice(0, 4) ? label : `${label}, ${year}`;
}
/** "about Nov 18" (Warren's words). */
export function aboutDay(ymd, now = new Date(), timeZone) {
    return `about ${formatDay(ymd, now, timeZone)}`;
}
const latest = (days) => days.sort().pop() ?? null;
const later = (a, b) => (a < b ? b : a);
export function expectedDates(input) {
    const now = input.now ?? new Date(), zone = datesTimeZone(input.time_zone), today = zonedDay(now, zone);
    const day = (d) => zonedDay(d, zone);
    const shipments = input.shipments ?? [];
    const lead = input.lead_time_days;
    const missing = lead.some(d => d === null || d === undefined || !Number.isFinite(d) || d < 0);
    const released = !!input.released_at;
    const transit = input.transit_days !== null && input.transit_days !== undefined && Number.isFinite(input.transit_days) && input.transit_days >= 0 ? Math.round(input.transit_days) : null;
    const ready = input.ready_date && YMD.test(input.ready_date) ? input.ready_date : null;
    let ship = null, delivery = null;
    const shipped = shipments.length > 0;
    const onTheWay = shipments.filter(s => !s.delivered_at);
    const delivered = shipped && onTheWay.length === 0;
    if (shipped) {
        ship = shipments.map(s => day(s.shipped_at)).sort()[0];
        if (!delivered) {
            const etas = onTheWay.flatMap(s => (s.eta ? [day(s.eta)] : []));
            const last = latest(onTheWay.map(s => day(s.shipped_at)));
            delivery = etas.length ? latest(etas) : transit !== null && last ? addDays(last, transit) : null;
            if (delivery)
                delivery = later(delivery, today);
        }
    }
    else if (ready || (lead.length && !missing)) {
        const from = released ? day(input.released_at) : today;
        ship = later(ready ?? addDays(from, Math.max(...lead.map(d => Math.round(d)))), today);
        delivery = transit !== null ? addDays(ship, transit) : null;
    }
    const about = (d) => aboutDay(d, now, zone);
    const pending = missing && !shipped && !ready;
    const ship_text = pending ? SHIP_DATE_PENDING_TEXT : !ship ? null : shipped ? `Shipped ${formatDay(ship, now, zone)}.` : `Expected to ship ${about(ship)}.`;
    const delivery_text = delivered || !ship ? null : delivery ? `Expected delivery ${about(delivery)}.` : shipped ? null : 'Delivery follows the freight quote.';
    return {
        ship_date: ship, delivery_date: delivery, released, shipped, delivered, missing_lead_time: pending,
        // Delivered: the timeline says so, and nothing is expected any more.
        ship_text, delivery_text, line: delivered ? null : [ship_text, delivery_text].filter(Boolean).join(' ') || null, time_zone: zone,
    };
}
//# sourceMappingURL=expectedDates.js.map